import React, { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'

import { OPageWrapper } from '@organisms/OPageWrapper'
import { signInWithEmailAndPassword } from '@services/Authentication'

import { useTranslations, useWhiteBackground } from '@hooks'

import logo from '@assets/images/logo.svg'

import { AButton } from '@atoms/AButton'
import { AFormInput } from '@atoms/AFormInput'
import { OResetPasswordModal } from '@organisms/OResetPasswordModal'
import { ROUTES } from '@const/Routes'

export const PSignInWithEmailAndPassword = () => {
  const { t } = useTranslations()

  useWhiteBackground()

  const [isWorking, setIsWorking] = useState(false)
  const [errorMsg, setErrorMsg] = useState()
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false)

  const { control, handleSubmit } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const handleSignIn = useCallback(async (email, password) => {
    try {
      setIsWorking(true)
      setErrorMsg(false)
      await signInWithEmailAndPassword({ email, password })
      setIsWorking(false)
    } catch (err) {
      setErrorMsg(t('error.signIn'))
      setIsWorking(false)
    }
  }, [])

  const onSubmit = useCallback(
    data => {
      handleSignIn(data.email, data.password)
    },
    [handleSignIn]
  )

  const handleOpenResetPasswordModal = useCallback(() => {
    setIsResetPasswordModalOpen(true)
  }, [])

  const handleCloseResetPasswordModal = useCallback(() => {
    setIsResetPasswordModalOpen(false)
  }, [])

  return (
    <OPageWrapper
      isHeaderVisible={false}
      className="!bg-white"
      contentClassName="flex flex-col items-center justify-between min-h-screen pb-8 lg:py-12"
    >
      <div className="mt-16 md:mt-18 flex flex-col items-center">
        {/*<div className="text-4xl md:text-5xl font-p22 font-bold">{t('logo')}</div>*/}
        <img src={logo} alt="logo" className="w-28 md:w-32 lg:w-40" />
        <h2 className="mt-16 mb-0.5 text-center font-p22 font-medium text-3xl">{t('welcome')}</h2>
        <form className="w-[300px] mx-auto mt-3 space-y-2.5" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="email"
            rules={{ required: t('required.email') }}
            render={({ field, fieldState }) => (
              <AFormInput
                {...field}
                id="email"
                placeholder={t('label.email')}
                type="email"
                autoComplete="email"
                errorMessage={fieldState.error && fieldState.error.message}
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            rules={{ required: t('required.password') }}
            render={({ field, fieldState }) => (
              <AFormInput
                {...field}
                id="password"
                placeholder={t('label.password')}
                type="password"
                autoComplete="current-password"
                errorMessage={fieldState.error && fieldState.error.message}
              />
            )}
          />

          <div className="pt-3 mx-8">
            <AButton className="w-full" type="submit" isLoading={isWorking}>
              {t('signIn')}
            </AButton>
          </div>
        </form>
        <div>
          {errorMsg && (
            <div className="mt-6 flex flex-1 flex-grow text-center">
              <p className="text-red-600 text-sm mx-auto">{errorMsg}</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col items-center justify-center mt-6">
        <div>
          <AButton
            onClick={handleOpenResetPasswordModal}
            outline
            small
            className="mx-auto px-8 font-light"
          >
            {t('resetPassword')}
          </AButton>
        </div>
        <Link
          to={ROUTES.SIGN_IN}
          className="relative mt-3 text-sm hover:underline font-medium rounded focus:outline-none focus:ring-2 focus:ring-inset focus:ring-codGray"
        >
          {t('signInWithPhoneNumber')}
        </Link>
      </div>

      <OResetPasswordModal
        isOpen={isResetPasswordModalOpen}
        onClose={handleCloseResetPasswordModal}
      />
    </OPageWrapper>
  )
}
